import { ModalRef, useFormBaseMetadata, useModuleFederationContext, useTranslation } from '@msdk/common'
import {
  ActionMenuBehaviour,
  EntityAcl,
  EntityContext,
  EntityMetadata,
  EntityModel,
  EntityOperation,
  FormMode,
  FormModel,
  FormNode,
  shouldBeShownCustomUIHeader,
} from '@msdk/definitions'
import {
  COLORS,
  createComponentWrapperWithRef,
  FormHeaderWrapper,
  styled,
  THEME_CONST,
  Typography,
  uiTokens,
} from '@msdk/ui'
import React from 'react'

import { findCenterGroupLeftSpacing, resolveItem, stringFormat } from '../../utils'
import { CustomLogicInvoker } from '../DynamicForm/custom-logic-invoker'
import { ImportedComponentErrorBoundary } from '../ImportedComponentBoundary'
import { FrameworkHeader } from './FrameworkHeader'
import { HeaderActions } from './HeaderActions'

const SimpleHeaderWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: ${COLORS.white.main};
`

const BaseHeaderContainer = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: start;
`

const MuiStyledReadonlyHeader = styled(BaseHeaderContainer)<{ isModal: boolean }>`
  padding-left: ${THEME_CONST.defaultPadding}px;
  padding-bottom: ${THEME_CONST.defaultPadding}px;
  background-color: ${COLORS.white.main};
`

const NovaStyledReadonlyHeader = styled(BaseHeaderContainer)<{ isModal: boolean }>`
  ${({ isModal }) => !isModal && `padding-left: ${uiTokens.spacing.padding.large}px;`};
  ${({ isModal }) => !isModal && `padding-bottom: ${uiTokens.spacing.padding.large}px;`};
`

const StyledReadonlyHeader = createComponentWrapperWithRef(
  {
    mui: MuiStyledReadonlyHeader,
    nova: NovaStyledReadonlyHeader,
  },
  'StyledReadonlyHeader',
)

const TitleFormMui = styled('div', { shouldForwardProp: (prop) => prop !== '$leftSpace' })<{
  $leftSpace: number
}>`
  padding-bottom: 16px;
  padding-top: 2px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-left: ${(props) => props.$leftSpace}%;
  width: 100%;
  padding-top: 24px;
  .modal-form-header & {
    padding-top: 8px;
    padding-bottom: 8px;
  }
`
const TitleFormNova = styled('div', { shouldForwardProp: (prop) => prop !== '$leftSpace' })<{
  $leftSpace: number
}>`
  padding-bottom: 0px;
  padding-top: 2px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-left: ${(props) => props.$leftSpace}%;
  width: 100%;
  padding-top: 24px;
  .modal-form-header & {
    padding-top: 8px;
    padding-bottom: 8px;
  }
`
const TitleForm = createComponentWrapperWithRef(
  {
    mui: TitleFormMui,
    nova: TitleFormNova,
  },
  'TitleForm',
)

const MuiTextHeaderWrapper = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 16px;
  padding-top: 2px;
  background-color: ${COLORS.white.main};
  padding-left: ${THEME_CONST.defaultPadding}px;
`

const NovaTextHeaderWrapper = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 16px;
  padding-top: 2px;
  padding-left: ${uiTokens.spacing.padding.large}px;
`

const TextHeaderWrapper = createComponentWrapperWithRef(
  {
    mui: MuiTextHeaderWrapper,
    nova: NovaTextHeaderWrapper,
  },
  'TextHeaderWrapper',
)

const MuiTextModalHeaderWrapper = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 16px;
  padding-top: 2px;
  background-color: ${COLORS.white.main};
  padding-left: ${THEME_CONST.defaultPadding}px;
`

const NovaTextModalHeaderWrapper = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0px;
  padding-top: 0px;
  background-color: ${uiTokens.uiSurface['wm-static']};
`

const TextHeaderModalWrapper = createComponentWrapperWithRef(
  {
    mui: MuiTextModalHeaderWrapper,
    nova: NovaTextModalHeaderWrapper,
  },
  'TextHeaderWrapper',
)

const MuiActionsWrapper = styled('div')`
  margin-right: auto;
  margin-top: 5px;
  padding-bottom: 16px;
`
const NovaActionsWrapper = styled('div')`
  margin-left: auto;
  margin-top: 5px;
  padding-bottom: 16px;
`
const ActionsWrapper = createComponentWrapperWithRef(
  {
    mui: MuiActionsWrapper,
    nova: NovaActionsWrapper,
  },
  'ActionsWrapper',
)

const MuiReadonlyHeaderTitle = styled(Typography)``

const NovaReadonlyHeaderTitle = styled(Typography)``

const ReadonlyModalHeaderTitle = styled(Typography)`
  font-weight: ${uiTokens.font.fontWeight.semiBold};
  font-size: ${uiTokens.font.fontSize.title.large}px;
  line-height: ${uiTokens.font.lineHeight.title.large}px;
  letter-spacing: ${uiTokens.font.letterSpacing.normal}px;
`

const ReadonlyHeaderTitle = createComponentWrapperWithRef(
  {
    mui: MuiReadonlyHeaderTitle,
    nova: NovaReadonlyHeaderTitle,
  },
  'ReadonlyHeaderTitle',
)

const StyledTypography = createComponentWrapperWithRef<{ children: React.ReactNode; modal?: ModalRef }>(
  {
    mui: ({ children }) => <Typography variant="h3">{children}</Typography>,
    nova: ({ children, modal }) => (
      <Typography variant={modal ? 'body1' : 'h3'} sx={{ fontWeight: uiTokens.font.fontWeight.semiBold }}>
        {children}
      </Typography>
    ),
  },
  'StyledTypography',
)

interface Props {
  onSave: (actionId?: string | undefined) => Promise<EntityOperation>
  onDelete: () => Promise<EntityOperation>
  onRestore: () => Promise<EntityOperation>
  onReloadEntity: () => Promise<void>
  onMoveToBin: () => Promise<EntityOperation>
  rebindEntitiy: (entity: EntityContext) => void
  entity: EntityContext
  entityModel: EntityModel
  formModel: FormModel
  rootNode: FormNode
  acl: EntityAcl
  invoker: CustomLogicInvoker
  mode: FormMode
  entityLoaded: boolean
  modalRef?: ModalRef
  onModeChange?: (mode: FormMode, formName: string) => void
}

export function FormHeader(props: Props) {
  const t = useTranslation()
  const isModal = !!props.modalRef
  const baseMetadata = useFormBaseMetadata(props.entityModel.name, props.formModel.name) as EntityMetadata
  const modules = useModuleFederationContext()
  const msModule = modules.loadedModules[baseMetadata.msProvider]
  const ImportedComponent = resolveItem(msModule, props.formModel.header?.customUI)

  const headerActions = (
    <HeaderActions
      acl={props.acl}
      onDelete={props.onDelete}
      entity={props.entity}
      invoker={props.invoker}
      onMoveToBin={props.onMoveToBin}
      onReloadEntity={props.onReloadEntity}
      onRestore={props.onRestore}
      onSave={props.onSave}
      entityModel={props.entityModel}
      formModel={props.formModel}
      rootNode={props.rootNode}
      mode={props.mode}
      entityLoaded={props.entityLoaded}
      rebindEntitiy={props.rebindEntitiy}
      modalRef={props.modalRef}
      onModeChange={props.onModeChange}
    />
  )

  const inCreation = props.entity.IsInCreation

  const defaultHeaderDescription = props.formModel.idml
    ? t(props.formModel.idml, props.formModel.description)
    : t(props.entityModel.idml, props.entityModel.description)

  const localization = inCreation
    ? {
        IDML: 'CMPSR_CREATE_NEW_ENTITY',
        fallback: 'Create {0}',
        headerDescription: defaultHeaderDescription,
      }
    : {
        IDML: 'CMPSR_EDIT_ENTITY',
        fallback: 'Edit {0}',
        headerDescription: defaultHeaderDescription,
      }

  const tryGetCustomHeader = () => {
    const { labels } = props.formModel
    if (inCreation && labels?.createHeader) {
      return t(labels.createHeader.idml, labels.createHeader.description)
    }

    if (!inCreation && labels?.editHeader) {
      return t(labels.editHeader.idml, labels.editHeader.description)
    }
  }

  return (
    <>
      {props.mode === 'Edit' && !props.formModel.header?.customUI && (
        <FormHeaderWrapper $mode={props.mode} $centered={props.formModel.isCentered()} $isModal={isModal}>
          <TitleForm $leftSpace={findCenterGroupLeftSpacing(props.formModel)}>
            <StyledTypography modal={props?.modalRef}>
              {props.entityLoaded &&
                (tryGetCustomHeader() ??
                  stringFormat(t(localization.IDML, localization.fallback), localization.headerDescription))}
            </StyledTypography>
          </TitleForm>
        </FormHeaderWrapper>
      )}
      {props.mode === 'Readonly' && !props.formModel.header && isModal && (
        <SimpleHeaderWrapper>
          <TextHeaderModalWrapper>
            <ReadonlyModalHeaderTitle variant="h3">
              {tryGetCustomHeader() ?? defaultHeaderDescription}
            </ReadonlyModalHeaderTitle>
          </TextHeaderModalWrapper>
          {props.formModel.actionMenuBehaviour === ActionMenuBehaviour.Default && (
            <ActionsWrapper>{headerActions}</ActionsWrapper>
          )}
        </SimpleHeaderWrapper>
      )}
      {props.mode === 'Readonly' && !props.formModel.header && !isModal && (
        <SimpleHeaderWrapper>
          <TextHeaderWrapper>
            <ReadonlyHeaderTitle variant="h3">{tryGetCustomHeader() ?? defaultHeaderDescription}</ReadonlyHeaderTitle>
          </TextHeaderWrapper>
          {props.formModel.actionMenuBehaviour === ActionMenuBehaviour.Default && (
            <ActionsWrapper>{headerActions}</ActionsWrapper>
          )}
        </SimpleHeaderWrapper>
      )}
      {shouldBeShownCustomUIHeader(props.mode, props.formModel) && ImportedComponent && (
        <StyledReadonlyHeader isModal={isModal}>
          <ImportedComponentErrorBoundary>
            <ImportedComponent
              entityId={props.entity.id}
              actions={headerActions}
              entityModel={props.entityModel}
              formModel={props.formModel}
              save={props.onSave}
              reloadEntity={props.onReloadEntity}
            />
          </ImportedComponentErrorBoundary>
        </StyledReadonlyHeader>
      )}
      {props.mode === 'Readonly' && props.formModel.header && !props.formModel.header?.customUI && (
        <FrameworkHeader
          header={props.formModel.header}
          entity={props.entity}
          entityModel={props.entityModel}
          formModel={props.formModel}
          actions={headerActions}
        />
      )}
    </>
  )
}
